import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Form as FormRoot } from "@/components/ui/form";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { InputField } from "../form/InputField";
import { ProfilePictureField } from "../form/ProfilePictureField";
import { handleFileUpload } from "@/utils/profileUtils";
import { useState } from "react";
import { BirthDateSelect } from "../form/BirthDateSelect";
import { childFormSchema, ChildFormData } from "@/types/childForm";
import { calculateAge, getInitialDate, formatBirthDate } from "@/utils/dateUtils";

interface ChildFormProps {
  mode: 'add' | 'edit';
  childId?: string;
  initialData?: {
    name: string;
    birthday?: string;
    profile_picture_url?: string | null;
  };
  onSuccess?: () => void;
}

export const ChildForm = ({ mode, childId, initialData, onSuccess }: ChildFormProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const initialBirthDate = getInitialDate(initialData?.birthday);

  const form = useForm<ChildFormData>({
    resolver: zodResolver(childFormSchema),
    defaultValues: {
      name: mode === 'edit' && initialData ? initialData.name : "",
      birthDate: initialBirthDate
    },
  });

  const onSubmit = async (values: ChildFormData) => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      toast({
        title: "Authentication Required",
        description: "Please log in to proceed",
        variant: "destructive",
      });
      return;
    }

    try {
      const formattedBirthDate = formatBirthDate(values.birthDate);
      const age = calculateAge(values.birthDate);

      const profilePictureUrl = selectedFile 
        ? await handleFileUpload(selectedFile, { toast }, initialData?.profile_picture_url)
        : initialData?.profile_picture_url;

      if (mode === 'add') {
        const { data, error } = await supabase
          .from("child_profiles")
          .insert({
            parent_id: session.user.id,
            name: values.name,
            age,
            birthday: formattedBirthDate,
            profile_picture_url: profilePictureUrl,
          })
          .select()
          .single();

        if (error) throw error;

        queryClient.setQueryData(["childProfiles"], (oldData: any) => {
          if (!oldData) return [data];
          return [...oldData, data];
        });
      } else if (mode === 'edit' && childId) {
        const { data, error } = await supabase
          .from("child_profiles")
          .update({
            name: values.name,
            age,
            birthday: formattedBirthDate,
            profile_picture_url: profilePictureUrl,
            updated_at: new Date().toISOString(),
          })
          .eq('id', childId)
          .eq('parent_id', session.user.id)
          .select()
          .single();

        if (error) throw error;

        queryClient.setQueryData(["childProfile", childId], data);
        queryClient.setQueryData(["childProfiles"], (oldData: any) => {
          if (!oldData) return [data];
          return oldData.map((profile: any) => 
            profile.id === childId ? data : profile
          );
        });
      }

      await queryClient.invalidateQueries({ queryKey: ["childProfiles"] });
      if (mode === 'edit') {
        await queryClient.invalidateQueries({ queryKey: ["childProfile", childId] });
      }

      toast({
        title: "Success",
        description: mode === 'add' ? "Child profile added successfully" : "Profile updated successfully",
      });

      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: error.message || "An error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <FormRoot {...form}>
      <form 
        onSubmit={form.handleSubmit(onSubmit)} 
        className="space-y-6" 
        noValidate
      >
        <InputField
          form={form}
          name="name"
          label="Name"
          placeholder="Enter child's name"
          autoFocus={false}
        />
        
        <div className="space-y-4">
          <BirthDateSelect form={form} />
        </div>

        <ProfilePictureField
          form={form}
          initialPreview={initialData?.profile_picture_url}
          onImageChange={setSelectedFile}
        />
        
        <Button 
          type="submit" 
          variant="default"
          className="w-full"
        >
          {mode === 'add' ? 'Add Child Profile' : 'Save Changes'}
        </Button>
      </form>
    </FormRoot>
  );
};
